import { useContext, useEffect, useRef, useState } from "react"

import { Dayjs } from "dayjs"

import { StatisticItem, useAnalyticsFilterInfo } from "@/components/Analytics/AnalyticsFilterInfo"
import { AnalyticsFiltersHeader, AnalyticsHeaderRefProps } from "@/components/Analytics/AnalyticsSettings/AnalyticsFiltersHeader"
import { CustomerContext } from "@/context/CustomerContext"

import {
  BaseDisplayUnit,
  baseDisplayUnitsMap,
  IAnalyticsPageDisplayUnit,
  AnalyticsFilterPeriods,
  AnalyticsPeriod,
  IAnalyticsSettingsOptions,
  AnalyticsOptionValue,
  AnalyticsDeliveryTypes,
  AnalyticsDefaultOptions,
  FilterType,
  BaseFilterTypes,
  BaseFilterTypesForAdmin
} from "@/types/IAnalytics"
import { DeliveryType } from "@/types/IOrder"


export interface AnalyticsFiltersProps {
  key?: string
  baseDisplayUnits: BaseDisplayUnit[]
  additionalDisplayUnits?: IAnalyticsPageDisplayUnit[]
  filterOptions?: FilterType[] | undefined
  statistics?: StatisticItem[]
}

export const useAnalyticsFilters = (props: AnalyticsFiltersProps) => {
  const {
    key,
    baseDisplayUnits,
    additionalDisplayUnits,
    filterOptions,
    statistics
  } = props

  /** all Tabs with charts */
  const displayUnits: IAnalyticsPageDisplayUnit[] = [
    ...baseDisplayUnits.map(u => baseDisplayUnitsMap[u]),
    ...(additionalDisplayUnits ?? [])
  ]

  const customerContext = useContext(CustomerContext)


  const currentFilterOptions = filterOptions ?? (customerContext.isCustomer() ? BaseFilterTypes : BaseFilterTypesForAdmin)


  const [optionsList, setOptionsList] = useState<IAnalyticsSettingsOptions[]>([AnalyticsDefaultOptions])
  const [draftOptionsList, setDraftOptionsList] = useState<IAnalyticsSettingsOptions[]>([AnalyticsDefaultOptions])

  const [compare, setCompare] = useState(false)
  const [draftCompare, setDraftCompare] = useState(false)
  /** current Tab with chart */
  const [displayUnit] = useState<IAnalyticsPageDisplayUnit>(displayUnits[0])
  const [deliveryType, setDeliveryType] = useState<DeliveryType>(DeliveryType.All)
  const [draftDeliveryType, setDraftDeliveryType] = useState<DeliveryType>(DeliveryType.All)

  const filtersComponentRef = useRef<AnalyticsHeaderRefProps>(null)

  useEffect(() => {
    if (key && key === "AnalyticsPricePage") {

      setDraftOptionsList(prev => {
        const newOptions = [...prev]
        newOptions.forEach(o => {
          o.depotDisabled = true
        })
        return newOptions
      })
    }
    else {
      setDraftOptionsList(prev => {
        const newOptions = [...prev]
        newOptions.forEach(o => {
          o.depotDisabled = false
        })
        return newOptions
      })
    }
  }, [key, optionsList])

  const {
    hasFiltersApplied,
    filterInfoAsText,
    filterInfoComponent,
    filterInfoOptions,
    getFilterInfoAsPlainText } = useAnalyticsFilterInfo({
      optionsList: optionsList,
      filterTypes: currentFilterOptions,
      statistics: statistics
    })

  const onCompareChange = (checked: boolean) => {
    const newOptionsCount = checked ? 2 : 1

    setDraftOptionsList(() => {
      const newOptionsList = draftOptionsList
      if (newOptionsCount === 1) {
        return [newOptionsList[0]]
      } else {
        const oldOptions = draftOptionsList[0]

        for (let i = 1; i < newOptionsCount; i++) {
          newOptionsList.push({
            ...oldOptions
          })
        }
      }

      return newOptionsList
    })
    setDraftCompare(checked)
  }

  const onDeliveryTypeChange = (deliveryType: DeliveryType) => {
    setDraftDeliveryType(deliveryType)
    setDraftOptionsList(prev => {
      const newOptions = [...prev]
      newOptions.forEach(o => {

        o.deliveryType = deliveryType
        o.deliveryTypeName = AnalyticsDeliveryTypes.find(p => p.value === deliveryType)?.label
      })
      return newOptions
    })
  }

  const getOnRangeChangeFunction = (optionsId: number) => {
    if (optionsId < 1)
      return

    return (dates: [start: Dayjs | null, end: Dayjs | null] | null, dateStrings: [string, string]) => {
      if (!dates || dates.length === 2) {
        setDraftOptionsList(prev => {
          const newOptions = [...prev]
          const thisOptions = newOptions[optionsId - 1]
          thisOptions.startDate = dates ? dates[0]?.format("YYYY-MM-DD") : undefined
          thisOptions.endDate = dates ? dates[1]?.format("YYYY-MM-DD") : undefined
          thisOptions.activePeriod = dates ? undefined : AnalyticsPeriod.Monthly
          thisOptions.activePeriodName = dates ? undefined : AnalyticsFilterPeriods.find(p => p.value === AnalyticsPeriod.Monthly)?.label
          return newOptions
        })
      }
    }
  }

  const getOnPeriodChangeFunction = (optionsId: number) => {
    if (optionsId < 1)
      return

    return (period: AnalyticsPeriod) => {
      if (!isNaN(period) && period >= 0) {
        setDraftOptionsList(prev => {
          const newOptions = [...prev]
          const thisOptions = newOptions[optionsId - 1]
          thisOptions.startDate = undefined
          thisOptions.endDate = undefined
          thisOptions.activePeriod = period
          thisOptions.activePeriodName = AnalyticsFilterPeriods.find(p => p.value === period)?.label
          return newOptions
        })
      }
    }
  }

  const getOptionLabel = (option: AnalyticsOptionValue | AnalyticsOptionValue[] | undefined): string | undefined => {
    if (!option || Array.isArray(option)) {
      return undefined
    }
    return option.label
  }

  const getOnFuelTypeChangeFunction = (optionsId: number) => {

    if (optionsId < 1)
      return

    return (fuelTypeId: number | undefined, option: AnalyticsOptionValue | AnalyticsOptionValue[]) => {
      setDraftOptionsList(prev => {
        const newOptions = [...prev]
        const thisOptions = newOptions[optionsId - 1]
        thisOptions.fuelTypeId = fuelTypeId
        thisOptions.fuelTypeName = getOptionLabel(option)
        return newOptions
      })
    }
  }

  const getOnDepotChangeFunction = (optionsId: number) => {
    if (optionsId < 1)
      return

    return (depotId: number | undefined, option: AnalyticsOptionValue | AnalyticsOptionValue[], isSetOptionsList?: boolean) => {
      if (isSetOptionsList) {
        setOptionsList(prev => {
          const newOptions = [...prev]
          const thisOptions = newOptions[optionsId - 1]
          thisOptions.depotId = depotId
          thisOptions.depotName = getOptionLabel(option)
          return newOptions
        })
      }
      setDraftOptionsList(prev => {
        const newOptions = [...prev]
        const thisOptions = newOptions[optionsId - 1]
        thisOptions.depotId = depotId
        thisOptions.depotName = getOptionLabel(option)
        return newOptions
      })
    }
  }

  const getOnCustomerClientChangeFunction = (optionsId: number) => {
    if (optionsId < 1)
      return

    return (clientId: number | undefined, option: AnalyticsOptionValue | AnalyticsOptionValue[]) => {
      setDraftOptionsList(prev => {
        const newOptions = [...prev]
        const thisOptions = newOptions[optionsId - 1]
        thisOptions.customerCategoryId = undefined
        thisOptions.customerCategoryName = undefined
        thisOptions.customerClientId = clientId
        thisOptions.customerClientName = getOptionLabel(option)
        return newOptions
      })
    }
  }

  const getOnCustomerCategoryChangeFunction = (optionsId: number) => {
    if (optionsId < 1)
      return

    return (customerCategoryId: number | undefined, option: AnalyticsOptionValue | AnalyticsOptionValue[]) => {
      setDraftOptionsList(prev => {
        const newOptions = [...prev]
        const thisOptions = newOptions[optionsId - 1]
        thisOptions.customerClientId = undefined
        thisOptions.customerClientName = undefined
        thisOptions.customerCategoryId = customerCategoryId
        thisOptions.customerCategoryName = getOptionLabel(option)
        return newOptions
      })
    }
  }

  const component =
    <AnalyticsFiltersHeader ref={filtersComponentRef}
      onCompareChange={onCompareChange}
      getOnPeriodChangeFunction={getOnPeriodChangeFunction}
      getOnRangeChangeFunction={getOnRangeChangeFunction}
      getOnFuelTypeChangeFunction={getOnFuelTypeChangeFunction}
      getOnDepotChangeFunction={getOnDepotChangeFunction}
      getOnCustomerClientChangeFunction={getOnCustomerClientChangeFunction}
      getOnCustomerCategoryChangeFunction={getOnCustomerCategoryChangeFunction}
      onDeliveryTypeChange={onDeliveryTypeChange}
      draftOptionsList={draftOptionsList}
      setOptionsList={setOptionsList}
      setDraftOptionsList={setDraftOptionsList}
      setCompare={setCompare}
      setDeliveryType={setDeliveryType}
      setDraftCompare={setDraftCompare}
      setDraftDeliveryType={setDraftDeliveryType}
      optionsList={optionsList}
      filterOptions={currentFilterOptions}
      isComparison={compare}
      draftIsComparison={draftCompare}
      deliveryType={draftDeliveryType} />

  return {
    optionsList,
    filtersComponentRef,
    filterComponent: component,
    displayUnit: displayUnit,
    isComparison: compare,
    deliveryType: deliveryType,
    hasFiltersApplied,
    filterInfoAsText,
    filterInfoComponent,
    filterInfoOptions,
    getFilterInfoAsPlainText
  }
}